import React from 'react';
import { motion } from 'framer-motion';
import { Phone, MessageCircle, Mail, MapPin, ChevronUp } from 'lucide-react';
import { mockCompanyInfo } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const quickLinks = [
    { name: 'בית', href: '#home' },
    { name: 'רכבים', href: '#cars' },
    { name: 'שירותים', href: '#services' },
    { name: 'אודות', href: '#about' },
    { name: 'צור קשר', href: '#contact' }
  ];

  const legalLinks = [
    { name: 'תנאי שימוש', href: '/terms' },
    { name: 'מדיניות פרטיות', href: '/privacy' },
    { name: 'הצהרת נגישות', href: '/accessibility' },
    { name: 'זכויות צרכן', href: '/consumer-rights' }
  ];

  return (
    <footer className="bg-luxury-surface border-t border-luxury-border relative">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          
          {/* Brand */}
          <div className="lg:col-span-1">
            <img 
              src="/images/jclass-logo.png" 
              alt="J CLASS MOTORS" 
              className="h-16 w-auto object-contain mb-6"
            />
            <p className="text-luxury-muted leading-relaxed mb-6">
              סוכנות רכבים מובילה המתמחה במכירה, קנייה והחלפה של רכבים מכל הסוגים.
            </p>
            
            {/* Contact Buttons */}
            <div className="flex gap-3">
              <motion.button
                onClick={() => window.open(`tel:${mockCompanyInfo.phone}`, '_self')}
                className="w-12 h-12 flex items-center justify-center bg-luxury-surface-light border border-luxury-border hover:border-luxury-gold hover:bg-luxury-gold text-luxury-text hover:text-luxury-black transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Phone className="w-5 h-5" />
              </motion.button>
              <motion.button
                onClick={() => {
                  const phoneNumber = mockCompanyInfo.whatsapp.replace(/[^0-9]/g, '');
                  window.open(`https://wa.me/972${phoneNumber.substring(1)}`, '_blank');
                }}
                className="w-12 h-12 flex items-center justify-center bg-green-600 hover:bg-green-500 text-white transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <MessageCircle className="w-5 h-5" />
              </motion.button>
              <motion.button
                onClick={() => window.location.href = `mailto:${mockCompanyInfo.email}`}
                className="w-12 h-12 flex items-center justify-center bg-luxury-surface-light border border-luxury-border hover:border-luxury-gold hover:bg-luxury-gold text-luxury-text hover:text-luxury-black transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mail className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-luxury-text font-bold mb-6 text-lg">ניווט מהיר</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => handleNavClick(link.href)}
                    className="text-luxury-muted hover:text-luxury-gold transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-luxury-text font-bold mb-6 text-lg">צור קשר</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-luxury-gold mt-1 flex-shrink-0" />
                <div>
                  <p className="text-luxury-muted text-sm">טלפון</p>
                  <a href={`tel:${mockCompanyInfo.phone}`} className="text-luxury-text hover:text-luxury-gold transition-colors">
                    {mockCompanyInfo.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-luxury-gold mt-1 flex-shrink-0" />
                <div>
                  <p className="text-luxury-muted text-sm">אימייל</p>
                  <a href={`mailto:${mockCompanyInfo.email}`} className="text-luxury-text hover:text-luxury-gold transition-colors">
                    {mockCompanyInfo.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-luxury-gold mt-1 flex-shrink-0" />
                <div>
                  <p className="text-luxury-muted text-sm">כתובת</p>
                  <p className="text-luxury-text">{mockCompanyInfo.address}</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-luxury-text font-bold mb-6 text-lg">מידע משפטי</h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-luxury-muted hover:text-luxury-gold transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-luxury-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-luxury-muted text-sm text-center md:text-right">
              © {currentYear} J CLASS MOTORS. כל הזכויות שמורות.
            </p>
            
            {/* Back to Top */}
            <motion.button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-luxury-muted hover:text-luxury-gold transition-colors duration-300 text-sm"
              whileHover={{ y: -2 }}
            >
              <span>חזרה למעלה</span>
              <ChevronUp className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>

      {/* Decorative Gold Line */}
      <div className="h-1 bg-gradient-to-r from-transparent via-luxury-gold to-transparent" />
    </footer>
  );
};

export default Footer;
